import Warning from "@/assets/js/warning";
import {
  IN_POSSESSION,
  OUT_OF_POSSESSION,
} from "@/assets/js/tactics";
import PartnershipTypes from "@/assets/js/partnership-types";


const getters = {
  warnings (modState, getters, state) {
    return [
      ...forwardMovementWarnings(getters.forwardMovementScores),
      ...partnershipWarnings(getters.partnerships, getters.coreStyle, getters.attackingStyle),
      ...playingStyleInstructionWarnings(),
      ...riskBalanceWarnings(getters.playingStyleAnalysis),
      ...overlapWarnings(state.instructions.inPossession.approachPlay, getters.partnershipCounts),
      ...positionalResponsiblityWarnings(getters.positionalResponsibilities),
      ...transitionInstructionWarnings(state.instructions.inTransition, getters),
      ...dutyCountWarnings(getters.positions().map(position => position.selectedRole.duty)),
      ...suitabilityWarnings(getters, state),
    ];
  }
};


const forwardMovementWarnings = (scores) => {
  const warnings = [];
  for (const [scoreName, calculation] of Object.entries(scores)) {
    if (calculation.score < calculation.target) {
      const warning = new Warning("WarningForwardMovement", {
        scoreName,
        calculatedScore: calculation.score,
        targetScore: calculation.target,
        side: null,
      });
      warnings.push(warning);
      continue;
    }

    for (const side of ["left", "right"]) {
      if (calculation[side] < calculation.target) {
        const warning = new Warning("WarningForwardMovement", {
          scoreName,
          calculatedScore: calculation[side],
          targetScore: calculation.target,
          side,
        });
        warnings.push(warning);
      }
    }
  }

  return warnings;
}


const partnershipWarnings = (partnerships, coreStyle, attackingStyle) => {
  if (!partnerships.centralMidfield) return [];
  const centralMidfieldPartnerships = partnerships.centralMidfield.filter(partnership => {
      if (partnership.partnershipType === "lowRiskDirect") return true;
      if (partnership.partnershipCategory === PartnershipTypes.balanced) return true;
      if (partnership.partnershipType.startsWith("mediumRisk")) return true;
      return false;
    });

  const warnings = [
    ...checkPartnerships(partnerships.centralAttack, 'centralAttack', coreStyle ,attackingStyle),
    ...checkPartnerships(centralMidfieldPartnerships, 'centralMidfield', coreStyle ,attackingStyle),
    ...checkPartnerships(partnerships.leftFlank, 'leftFlank', coreStyle ,attackingStyle),
    ...checkPartnerships(partnerships.rightFlank, 'rightFlank', coreStyle ,attackingStyle),
  ];

  return warnings;
}


const playingStyleInstructionWarnings = () => {
  //TODO: check instructions are suitable for playing style
  const warnings = [];
  return warnings;
}


const riskBalanceWarnings = (playingStyleAnalysis) => {
  const warnings = [];

  if (playingStyleAnalysis.netInPossessionScore > 20.5 || playingStyleAnalysis.netInPossessionScore < -20.5) {
    warnings.push(new Warning(
      "WarningTacticalRisk",
      {
        possessionType: IN_POSSESSION,
        netRisk: playingStyleAnalysis.netInPossessionScore,
      }
    ));
  }

  if (playingStyleAnalysis.netOutOfPossessionScore > 20.5 || playingStyleAnalysis.netOutOfPossessionScore < -20.5) {
    warnings.push(new Warning(
      "WarningTacticalRisk",
      {
        possessionType: OUT_OF_POSSESSION,
        netRisk: playingStyleAnalysis.netInPossessionScore,
      }
    ));
  }

  return warnings;
}


const overlapWarnings = (approachPlayInstructions, partnershipCounts) => {
  const warnings = [];

  for (const side of ['Left', 'Right']) {
    let overlappingPartnerCount = 0;
    for (const [partnershipType, countObject] of Object.entries(partnershipCounts[`${side.toLowerCase()}Flank`])) {
      if (!partnershipType.includes("Overlapping")) continue;
      overlappingPartnerCount += Object.values(countObject).reduce((p, n) => p + n);
    }

    if (approachPlayInstructions[`overlap${side}`] !== 0 && overlappingPartnerCount === 0) {
      warnings.push(new Warning(
        'WarningOverlap',
        {
          overlapType: approachPlayInstructions[`overlap${side}`] > 0 ? 'overlap' : 'underlap',
          side: side.toLowerCase(),
        }
      ))
    }
  }

  return warnings;
}


const positionalResponsiblityWarnings = (positionalResponsibilities) => {
  const warnings = [];

  for (const [position, responsibilities] of Object.entries(positionalResponsibilities)) {
    for (const [responsibilityName, responsibility] of Object.entries(responsibilities)) {
      if (!responsibility.performed) {
        warnings.push(new Warning(
          'WarningResponsibilities',
          {
            caveats: false, //TODO
            pitchArea: position,
            responsibility: responsibilityName,
          }
        ))
      }
    }
  }

  return warnings;
}


const transitionInstructionWarnings = (transitionInstructions, getters) => {
  const warnings = [];
  
  const holdShape = transitionInstructions.attackingTransition < 0;
  const regroup = transitionInstructions.defensiveTransition < 0;
  const counter = transitionInstructions.attackingTransition > 0;
  const counterPress = transitionInstructions.defensiveTransition > 0;

  /**
   *  - check partnership types are compatible with chosen transition
   *    + more direct partnerships means quicker transitions and is fitting for counter press and counter
   *    + more overlapping partnerships means slower transitions and is fitting for regroup and hold shape
   */
  const partnershipTypeCounts = getters.partnershipCounts.total;
  if (partnershipTypeCounts.overlapping > partnershipTypeCounts.direct + 1) {
    const instructions = [];
    if (counter) {
      instructions.push({
        transitionType: 'attacking',
        instruction: 'counter',
      })
    }
    if (counterPress) {
      instructions.push({
        transitionType: 'defensive',
        instruction: 'counterPress',
      })
    }

    if (instructions.length) {
      warnings.push(new Warning(
        "WarningTransitionPartnerships",
        {
          mainPartnershipType: PartnershipTypes.overlapping,
          instructions,
        }
      ));
    }
  }

  if (partnershipTypeCounts.direct > partnershipTypeCounts.overlapping + 1) {
    const instructions = [];
    if (holdShape) {
      instructions.push({
        transitionType: 'attacking',
        instruction: 'holdShape',
      })
    }
    if (regroup) {
      instructions.push({
        transitionType: 'defensive',
        instruction: 'regroup',
      })
    }

    if (instructions.length) {
      warnings.push(new Warning(
        "WarningTransitionPartnerships",
        {
          mainPartnershipType: PartnershipTypes.overlapping,
          instructions,
        }
      ));
    }
  }

  /**
   *  - check attacking and defensive risk is fitting for the chosen transitions
   *    + lower attacking risk: regroup is maybe not good, opportunity to quickly recover ball is lost, because of quicker defensive transition - particularly if team has good relative mobility
   *    + higher attacking risk: counter press is maybe not good, because of slow defensive transition
   *    + lower defensive risk: hold shape is maybe not good, opportunity to counter thanks to quick attacking transition is lost - particularly if team has good relative mobility
   *    + higher defensive risk: counter is maybe not good, because of the slower attacking transition
   */

  const attackingRisk = getters.playingStyleAnalysis.netInPossessionScore;
  const defensiveRisk = getters.playingStyleAnalysis.netOutOfPossessionScore;

  if (attackingRisk < 0 && regroup) {
    warnings.push(new Warning(
      "WarningTransitionRisk",
      {
        transitionType: "defensive",
        instruction: "regroup",
        riskLevel: attackingRisk,
        riskType: "attacking",
      }
    ));
  }

  if (attackingRisk > 0 && counterPress) {
    warnings.push(new Warning(
      "WarningTransitionRisk",
      {
        transitionType: "defensive",
        instruction: "counterPress",
        riskLevel: attackingRisk,
        riskType: "attacking",
      }
    ));
  }

  if (defensiveRisk < 0 && holdShape) {
    warnings.push(new Warning(
      "WarningTransitionRisk",
      {
        transitionType: "attacking",
        instruction: "holdShape",
        riskLevel: defensiveRisk,
        riskType: "defensive",
      }
    ));
  }

  if (defensiveRisk > 0 && counter) {
    warnings.push(new Warning(
      "WarningTransitionRisk",
      {
        transitionType: "attacking",
        instruction: "counter",
        riskLevel: defensiveRisk,
        riskType: "defensive",
      }
    ));
  }

  return warnings;
}


const checkPartnerships = (partnershipList, pitchArea, coreStyle, attackingStyle) => {
  const warnings = [];

  if (partnershipList.length < 1) {
    warnings.push(new Warning('WarningMissingPartnership', { pitchArea }));
  } else {
    const suitable = partnershipList.some((partnership) => {
      return partnership.suitableForPlayingStyle(coreStyle, attackingStyle);
    });
    if (!suitable) {
      warnings.push(new Warning('WarningNoSuitablePartnership', { pitchArea }));
    }
  }

  return warnings;
}


const dutyCountWarnings = (selectedDuties) => {
  const warnings = [];

  const dutyCounts = {
    attack: 0,
    support: 0,
    defend: 0,
  };
  selectedDuties.map(duty => duty.toLowerCase()).forEach(duty => {
    const key = ["stopper", "cover"].includes(duty) ? "defend" : duty;
    dutyCounts[key] = dutyCounts[key] + 1;
  });

  for (const [duty, count] of Object.entries(dutyCounts)) {
    const expected = duty === "defend" ? 3 : 2;
    if (count < expected) {
      warnings.push(new Warning("WarningDutiesCount", {duty, count, expected}));
    }
  }

  return warnings;
}


const suitabilityWarnings = (getters, state) => {
  const warnings = [];

  // check for null values -> no team data loaded
  const nullValues = Object.values(state.teamSuitability).some((group) => {
    return Object.values(group).some(value => value === null);
  });
  if (nullValues) return [];

  // core style
  if (state.instructions.mentality === -2 && state.teamSuitability.coreStyle.defensive < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "core",
      selectedStyle: "veryDefensive",
      suitability: state.teamSuitability.coreStyle.defensive,
    }));
  } else if (state.instructions.mentality === -1 && state.teamSuitability.coreStyle.defensive < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "core",
      selectedStyle: "defensive",
      suitability: state.teamSuitability.coreStyle.defensive,
    }));
  }else if (state.instructions.mentality === 0 && state.teamSuitability.coreStyle.balanced < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "core",
      selectedStyle: "balanced",
      suitability: state.teamSuitability.coreStyle.balanced,
    }));
  } else if (state.instructions.mentality === 1 && state.teamSuitability.coreStyle.attacking < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "core",
      selectedStyle: "attacking",
      suitability: state.teamSuitability.coreStyle.attacking,
    }));
  } else if (state.instructions.mentality === 2 && state.teamSuitability.coreStyle.attacking < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "core",
      selectedStyle: "veryAttacking",
      suitability: state.teamSuitability.coreStyle.attacking,
    }));
  }

  // attacking style
  if (state.instructions.inPossession.passing === -2 && state.teamSuitability.attackingStyle.short < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "attacking",
      selectedStyle: "veryShort",
      suitability: state.teamSuitability.attackingStyle.short,
    }));
  } else if (state.instructions.inPossession.passing === -1 && state.teamSuitability.attackingStyle.short < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "attacking",
      selectedStyle: "short",
      suitability: state.teamSuitability.attackingStyle.short,
    }));
  } else if (state.instructions.inPossession.passing === 0 && state.teamSuitability.attackingStyle.mixed < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "attacking",
      selectedStyle: "mixed",
      suitability: state.teamSuitability.attackingStyle.mixed,
    }));
  } else if (state.instructions.inPossession.passing === 1 && state.teamSuitability.attackingStyle.direct < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "attacking",
      selectedStyle: "direct",
      suitability: state.teamSuitability.attackingStyle.direct,
    }));
  } else if (state.instructions.inPossession.passing === 2 && state.teamSuitability.attackingStyle.direct < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "attacking",
      selectedStyle: "veryDirect",
      suitability: state.teamSuitability.attackingStyle.direct,
    }));
  }

  // defensive style
  if (state.instructions.outOfPossession.markingAndTackling.pressingUrgency === -2 && state.teamSuitability.defensiveStyle.cautious < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "defensive",
      selectedStyle: "veryCautious",
      suitability: state.teamSuitability.defensiveStyle.cautious,
    }));
  } else if (state.instructions.outOfPossession.markingAndTackling.pressingUrgency === -1 && state.teamSuitability.defensiveStyle.cautious < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "defensive",
      selectedStyle: "cautious",
      suitability: state.teamSuitability.defensiveStyle.cautious,
    }));
  } else if (state.instructions.outOfPossession.markingAndTackling.pressingUrgency === 0 && state.teamSuitability.defensiveStyle.mixed < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "defensive",
      selectedStyle: "mixed",
      suitability: state.teamSuitability.defensiveStyle.mixed,
    }));
  } else if (state.instructions.outOfPossession.markingAndTackling.pressingUrgency === 1 && state.teamSuitability.defensiveStyle.aggressive < 50) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "defensive",
      selectedStyle: "aggressive",
      suitability: state.teamSuitability.defensiveStyle.aggressive,
    }));
  } else if (state.instructions.outOfPossession.markingAndTackling.pressingUrgency === 2 && state.teamSuitability.defensiveStyle.aggressive < 75) {
    warnings.push(new Warning("WarningUnsuitableStyle", {
      style: "defensive",
      selectedStyle: "veryAggressive",
      suitability: state.teamSuitability.defensiveStyle.aggressive,
    }));
  }

  // crossing
  // TODO: the crossing instructions UI isn't implemented yet!
  // if (getters.instructions.crossing === "floated" && state.teamSuitability.crossing.floated < 50) {
  //   warnings.push(new Warning("WarningUnsuitableInstruction", {}));
  // } else if (getters.instructions.crossing === "whipped" && state.teamSuitability.crossing.whipped < 50) {
  //   warnings.push(new Warning("WarningUnsuitableInstruction", {}));
  // } else if (getters.instructions.crossing === "low" && state.teamSuitability.crossing.low < 50) {
  //   warnings.push(new Warning("WarningUnsuitableInstruction", {}));
  // } else if (getters.instructions.crossing === "mixed" && state.teamSuitability.crossing.mixed < 50) {
  //   warnings.push(new Warning("WarningUnsuitableInstruction", {}));
  // }

  // transitions
  if (state.instructions.inTransition.defensiveTransition > 0 && state.teamSuitability.inTransition.counterPress < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "transitions.defensive.counterPress",
      suitability: state.teamSuitability.inTransition.counterPress,
    }));
  } else if (state.instructions.inTransition.defensiveTransition < 0 && state.teamSuitability.inTransition.regroup < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "transitions.defensive.regroup",
      suitability: state.teamSuitability.inTransition.regroup,
    }));
  }
  if (state.instructions.inTransition.attackingTransition > 0 && state.teamSuitability.inTransition.counter < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "transitions.attacking.counter",
      suitability: state.teamSuitability.inTransition.counter,
    }));
  } else if (state.instructions.inTransition.attackingTransition < 0 && state.teamSuitability.inTransition.holdShape < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "transitions.attacking.holdShape",
      suitability: state.teamSuitability.inTransition.holdShape,
    }));
  }

      // distributeQuickly: null, -> TODO UI not implemented

      // rollItOut: null, -> TODO UI not implemented
      // throwItLong: null, -> TODO UI not implemented
      // takeShortKicks: null, -> TODO UI not implemented
      // takeLongKicks: null, -> TODO UI not implemented

  // in possession
  if (state.instructions.inPossession.attackingWidth.attackingWidth === -1 && state.teamSuitability.inPossession.attackingWidth < 20) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.attackingWidth.narrow", 
      suitability: state.teamSuitability.inPossession.attackingWidth,
    }));
  } else if (state.instructions.inPossession.attackingWidth.attackingWidth === 0 && state.teamSuitability.inPossession.attackingWidth < 40) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.attackingWidth.standard", 
      suitability: state.teamSuitability.inPossession.attackingWidth,
    }));
  } else if (state.instructions.inPossession.attackingWidth.attackingWidth === 1 && state.teamSuitability.inPossession.attackingWidth < 60) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.attackingWidth.wide", 
      suitability: state.teamSuitability.inPossession.attackingWidth,
    }));
  } else if (state.instructions.inPossession.attackingWidth.attackingWidth === 2 && state.teamSuitability.inPossession.attackingWidth < 80) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.attackingWidth.extremelyWide", 
      suitability: state.teamSuitability.inPossession.attackingWidth,
    }));
  }
      
  if (state.instructions.inPossession.approachPlay.passIntoSpace && state.teamSuitability.inPossession.passIntoSpace < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.approachPlay.passIntoSpace", 
      suitability: state.teamSuitability.inPossession.passIntoSpace,
    }));
  }

  if (state.instructions.inPossession.approachPlay.playOutOfDefence && state.teamSuitability.inPossession.playOutOfDefence < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.approachPlay.playOutOfDefence", 
      suitability: state.teamSuitability.inPossession.playOutOfDefence,
    }));
  }
  
  if (state.instructions.inPossession.tempo === -1 && state.teamSuitability.inPossession.higherTempo < 20) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.tempo.lower", 
      suitability: state.teamSuitability.inPossession.higherTempo,
    }));
  } else if (state.instructions.inPossession.tempo === 0 && state.teamSuitability.inPossession.higherTempo < 40) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.tempo.standard", 
      suitability: state.teamSuitability.inPossession.higherTempo,
    }));
  } else if (state.instructions.inPossession.tempo === 1 && state.teamSuitability.inPossession.higherTempo < 60) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.tempo.higher", 
      suitability: state.teamSuitability.inPossession.higherTempo,
    }));
  } else if (state.instructions.inPossession.tempo === 2 && state.teamSuitability.inPossession.higherTempo < 80) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.tempo.muchHigher", 
      suitability: state.teamSuitability.inPossession.higherTempo,
    }));
  }

  if (state.instructions.inPossession.finalThird.workBallIntoBox && state.teamSuitability.inPossession.workBallIntoBox < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.finalThird.workBallIntoBox", 
      suitability: state.teamSuitability.inPossession.workBallIntoBox,
    }));
  }
  if (state.instructions.inPossession.finalThird.hitEarlyCrosses && state.teamSuitability.inPossession.hitEarlyCrosses < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.finalThird.hitEarlyCrosses", 
      suitability: state.teamSuitability.inPossession.hitEarlyCrosses,
    }));
  }
  if (state.instructions.inPossession.finalThird.shootOnSight && state.teamSuitability.inPossession.shootOnSight < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.finalThird.shootOnSight", 
      suitability: state.teamSuitability.inPossession.shootOnSight,
    }));
  }
      // runAtDefence: null, -> TODO UI not implemented
  
  if (state.instructions.inPossession.creativeFreedom === 1 && state.teamSuitability.inPossession.beMoreExpressive < 60) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "inPossession.creativeFreedom.more", 
      suitability: state.teamSuitability.inPossession.beMoreExpressive,
    }));
  }

  // out of possession
  if (state.instructions.outOfPossession.defensiveShape.useOffsideTrap && state.teamSuitability.outOfPossession.useOffsideTrap < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.useOffsideTrap", 
      suitability: state.teamSuitability.outOfPossession.useOffsideTrap,
    }));
  }
  
  if (state.instructions.outOfPossession.defensiveShape.engagementLine === -1 && state.teamSuitability.outOfPossession.lineOfEngagement < 20) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.lineOfEngagement.lower", 
      suitability: state.teamSuitability.outOfPossession.lineOfEngagement,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.engagementLine === 0 && state.teamSuitability.outOfPossession.lineOfEngagement < 40) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.lineOfEngagement.standard", 
      suitability: state.teamSuitability.outOfPossession.lineOfEngagement,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.engagementLine === 1 && state.teamSuitability.outOfPossession.lineOfEngagement < 60) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.lineOfEngagement.higher", 
      suitability: state.teamSuitability.outOfPossession.lineOfEngagement,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.engagementLine === 1 && state.teamSuitability.outOfPossession.lineOfEngagement < 80) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.lineOfEngagement.muchHigher", 
      suitability: state.teamSuitability.outOfPossession.lineOfEngagement,
    }));
  }

  if (state.instructions.outOfPossession.defensiveShape.defensiveLine === -1 && state.teamSuitability.outOfPossession.defensiveLine < 20) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveLine.lower", 
      suitability: state.teamSuitability.outOfPossession.defensiveLine,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.defensiveLine === 0 && state.teamSuitability.outOfPossession.defensiveLine < 40) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveLine.standard", 
      suitability: state.teamSuitability.outOfPossession.defensiveLine,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.defensiveLine === 1 && state.teamSuitability.outOfPossession.defensiveLine < 60) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveLine.higher", 
      suitability: state.teamSuitability.outOfPossession.defensiveLine,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.defensiveLine === 1 && state.teamSuitability.outOfPossession.defensiveLine < 80) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveLine.muchHigher", 
      suitability: state.teamSuitability.outOfPossession.defensiveLine,
    }));
  }
  
  if (state.instructions.outOfPossession.defensiveShape.defensiveWidth === 0 && state.teamSuitability.outOfPossession.defensiveWidth < 33.33) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveWidth.standard", 
      suitability: state.teamSuitability.outOfPossession.defensiveWidth,
    }));
  } else if (state.instructions.outOfPossession.defensiveShape.defensiveWidth === 1 && state.teamSuitability.outOfPossession.defensiveWidth < 66.66) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.defensiveShape.defensiveWidth.wider", 
      suitability: state.teamSuitability.outOfPossession.defensiveWidth,
    }));
  }

  if (state.instructions.outOfPossession.markingAndTackling.useTighterMarking && state.teamSuitability.outOfPossession.tighterMarking < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.markingAndTackling.tighterMarking", 
      suitability: state.teamSuitability.outOfPossession.tighterMarking,
    }));
  }
  
  if (state.instructions.outOfPossession.markingAndTackling.tackling > 0 && state.teamSuitability.outOfPossession.getStuckIn < 50) {
    warnings.push(new Warning("WarningUnsuitableInstruction", {
      instruction: "outOfPossession.markingAndTackling.tackling.getStuckIn", 
      suitability: state.teamSuitability.outOfPossession.getStuckIn,
    }));
  }


  return warnings;
}


export default {
  getters
}
